import React from 'react';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY, normalizeMainTheme, parseTypologyTag } from '../data/taxonomies.js';

// ============================================================
// Filter chip rail, related orgs, helpful empty state
// ============================================================

export function FilterChipRail({
  search, setSearch,
  selectedActivities, toggleActivity, toggleMainActivity,
  selectedThemes, toggleTheme, toggleMainTheme,
  selectedCountries, toggleCountry,
  selectedScales, setSelectedScales,
  selectedPolicyProximity, setSelectedPolicyProximity,
  selectedImplementation, setSelectedImplementation,
  selectedTypes, setSelectedTypes,
  selectedStakeholders, setSelectedStakeholders,
  clearAll
}) {
  const removeFromSet = (setFn) => (val) => {
    setFn(prev => {
      const n = new Set(prev);
      n.delete(val);
      return n;
    });
  };
  const removeActivity = (val) => {
    // handle main category toggles by clearing all subs too
    const mainCat = ACTIVITY_TYPOLOGY.find(c => c.name === val);
    if (mainCat) toggleMainActivity(mainCat); else toggleActivity(val);
  };
  const removeTheme = (val) => {
    const mainCat = THEME_TYPOLOGY.find(c => c.name === val);
    if (mainCat) toggleMainTheme(mainCat); else toggleTheme(val);
  };

  const formatLensLabel = (v) => {
    const m = v.match(/^(\d+)\s*-\s*(.+)$/);
    return m ? `${m[1]}. ${m[2]}` : v;
  };

  const chips = [];
  if (search) chips.push({ key: 'q:'+search, label: `"${search}"`, kind: 'Search', onRemove: () => setSearch('') });
  selectedActivities.forEach(a => chips.push({ key: 'a:'+a, label: a, kind: 'Activity', onRemove: () => removeActivity(a) }));
  selectedThemes.forEach(t => chips.push({ key: 't:'+t, label: t, kind: 'Theme', onRemove: () => removeTheme(t) }));
  selectedCountries.forEach(c => chips.push({ key: 'c:'+c, label: c, kind: 'Country', onRemove: () => toggleCountry(c) }));
  selectedScales.forEach(s => chips.push({ key: 's:'+s, label: s.replace(' (e.g., EU-wide)', ''), kind: 'Scale', onRemove: () => removeFromSet(setSelectedScales)(s) }));
  selectedPolicyProximity.forEach(p => chips.push({ key: 'p:'+p, label: formatLensLabel(p), kind: 'Policy', onRemove: () => removeFromSet(setSelectedPolicyProximity)(p) }));
  selectedImplementation.forEach(p => chips.push({ key: 'i:'+p, label: formatLensLabel(p), kind: 'Implementation', onRemove: () => removeFromSet(setSelectedImplementation)(p) }));
  selectedTypes.forEach(t => chips.push({ key: 'ty:'+t, label: t, kind: 'Type', onRemove: () => removeFromSet(setSelectedTypes)(t) }));
  selectedStakeholders.forEach(s => chips.push({ key: 'sh:'+s, label: s, kind: 'Stakeholder', onRemove: () => removeFromSet(setSelectedStakeholders)(s) }));

  if (chips.length === 0) return null;
  return (
    <div className="chip-rail">
      <div className="chip-rail-inner">
        <span className="chip-rail-label">Filtering by:</span>
        {chips.map(c => (
          <button key={c.key} className="filter-chip" onClick={c.onRemove}>
            <span className="filter-chip-kind">{c.kind}</span>
            <span className="filter-chip-label">{c.label}</span>
            <span className="filter-chip-x" aria-hidden="true">
              <svg viewBox="0 0 12 12" width="10" height="10"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </span>
          </button>
        ))}
        {chips.length > 1 && <button className="filter-chip-clear" onClick={clearAll}>Clear all</button>}
      </div>
    </div>
  );
}
// ----------- Related orgs (shared themes & activities only) -----------

// Main themes/activities for an org, normalized so they line up with the
// typology colour maps used elsewhere.
export function orgThemeMains(o) {
  return new Set(o.themes.map(t => {
    const p = parseTypologyTag(t);
    return p?.main ? normalizeMainTheme(p.main) : null;
  }).filter(Boolean));
}
export function orgActivityMains(o) {
  return new Set(o.activities.map(a => parseTypologyTag(a)?.main).filter(Boolean));
}

export function computeRelated(org, allOrgs, n = 5) {
  const orgThemes = orgThemeMains(org);
  const orgActs = orgActivityMains(org);
  const scored = [];
  for (const o of allOrgs) {
    if (o.id === org.id) continue;
    const oThemes = orgThemeMains(o);
    const oActs = orgActivityMains(o);
    const sharedThemes = [...orgThemes].filter(t => oThemes.has(t));
    const sharedActs = [...orgActs].filter(a => oActs.has(a));
    if (sharedThemes.length === 0 && sharedActs.length === 0) continue;
    // Themes weigh more than activities; more overlap ranks higher.
    const score = sharedThemes.length * 6 + sharedActs.length * 2;
    scored.push({ org: o, score, sharedThemes, sharedActs });
  }
  scored.sort((a, b) => b.score - a.score || a.org.name.localeCompare(b.org.name));
  return scored.slice(0, n);
}

export function RelatedOrgs({ org, allOrgs, onSelectOrg, n = 5 }) {
  const related = React.useMemo(() => computeRelated(org, allOrgs, n), [org, allOrgs, n]);
  if (related.length === 0) return null;
  return (
    <div className="related-orgs">
      <div className="section-label">Related organisations</div>
      <div className="related-orgs-grid">
        {related.map(({ org: r, sharedThemes, sharedActs }) => (
          <button key={r.id} className="related-card" onClick={() => onSelectOrg(r)}>
            <div className="related-card-name">{r.name}</div>
            <div className="related-card-country">{r.country}</div>
            {r.mission && <div className="related-card-mission">{r.mission.slice(0, 100)}{r.mission.length > 100 ? '…' : ''}</div>}
            <div className="related-card-reasons">
              {sharedActs.map(a => {
                const cat = ACTIVITY_TYPOLOGY.find(c => c.name === a);
                return <span key={'a'+a} className="related-tag" style={{ background: cat?.color || '#ddd' }}>{a}</span>;
              })}
              {sharedThemes.map(t => {
                const cat = THEME_TYPOLOGY.find(c => c.name === t);
                return <span key={'t'+t} className="related-tag" style={{ background: cat?.color || '#ddd' }}>{t}</span>;
              })}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
// ----------- Empty state -----------

export function EmptyState({ activeFilterChips, clearAll, onNavigate }) {
  // activeFilterChips: array of { kind, label, onRemove } — same shape we use for the rail
  const top = (activeFilterChips || []).slice(0, 3);
  return (
    <div className="empty-state-rich">
      <div className="empty-state-art" aria-hidden="true">
        <svg viewBox="0 0 120 120" width="120" height="120">
          <circle cx="60" cy="60" r="48" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
          <circle cx="60" cy="60" r="32" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
          <circle cx="60" cy="60" r="14" fill="currentColor" opacity="0.18"/>
          <line x1="60" y1="6" x2="60" y2="18" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
          <line x1="60" y1="102" x2="60" y2="114" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
          <line x1="6" y1="60" x2="18" y2="60" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
          <line x1="102" y1="60" x2="114" y2="60" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
        </svg>
      </div>
      <h3 className="empty-state-h">No matches yet.</h3>
      <p className="empty-state-p">
        Your current filters didn't surface anything. Try loosening one of these:
      </p>
      {top.length > 0 && (
        <div className="empty-state-chips">
          {top.map(c => (
            <button key={c.kind + ':' + c.label} className="filter-chip" onClick={c.onRemove}>
              <span className="filter-chip-kind">Remove {c.kind}</span>
              <span className="filter-chip-label">{c.label}</span>
              <span className="filter-chip-x"><svg viewBox="0 0 12 12" width="10" height="10"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg></span>
            </button>
          ))}
        </div>
      )}
      <div className="empty-state-actions">
        <button className="btn-secondary" onClick={clearAll}>Clear all filters</button>
        <a className="btn-primary" href="#/submit" onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('submit'); } }}>
          Add your organisation →
        </a>
      </div>
      <p className="empty-state-foot">
        Know an organisation that should be here? Suggest it — we add new entries every month.
      </p>
    </div>
  );
}
